import React, { useState, useEffect } from 'react';
import { THEME } from '../../theme';
import { X, Activity, Cpu, Calendar, Mail, Clock, MessageCircle, Phone, Zap, Mic, ShieldCheck } from 'lucide-react';

const CapyBotMascot = ({ darkMode, onDeployClick, isLoggedIn }) => {
    const [mascotState, setMascotState] = useState(isLoggedIn ? 'happy' : 'standby');
    const [showThankYouMessage, setShowThankYouMessage] = useState(false);
  
    useEffect(() => {
      if (isLoggedIn) { setMascotState('happy'); setShowThankYouMessage(true); } 
      else { setMascotState('standby'); setShowThankYouMessage(false); }
    }, [isLoggedIn]);
  
    const handleReset = () => { setMascotState('standby'); };
    
    const handleMascotClick = () => {
        if (mascotState === 'standby') {
            setMascotState('active');
        }
    };
  
    const icons = [ { Icon: Calendar, color: THEME.secondary }, { Icon: Mail, color: THEME.primary }, { Icon: Clock, color: THEME.secondary }, { Icon: MessageCircle, color: THEME.secondary }, { Icon: Phone, color: THEME.primary }, { Icon: Zap, color: '#FFD700' }, { Icon: Mic, color: '#FF5757' }, { Icon: ShieldCheck, color: '#4ADE80' } ];
    const accentColor = THEME.secondary;
  
    return (
      <div className="relative w-full h-full select-none" onClick={handleMascotClick}>
        
        {showThankYouMessage && isLoggedIn && (
          <div className="absolute top-[15%] left-1/2 -translate-x-1/2 z-30 w-72 lg:w-80 animate-bounce-in">
             <div className="bg-white dark:bg-slate-900 backdrop-blur-xl border border-slate-200 dark:border-white/10 p-6 rounded-3xl shadow-2xl relative overflow-hidden group">
                <button onClick={() => setShowThankYouMessage(false)} className="absolute top-3 right-3 w-7 h-7 flex items-center justify-center rounded-full bg-slate-100 dark:bg-slate-800 text-slate-400"><X size={14} /></button>
                <div className="text-center relative z-10"><div className="text-5xl mb-4 animate-bounce">🎉</div><h3 className="text-slate-900 dark:text-white font-medium text-xl mb-2">Welcome to the Family!</h3><p className="text-slate-500 dark:text-slate-400 text-sm mb-3 font-light">Thank you for joining the waiting list!</p></div>
             </div>
          </div>
        )}
        {mascotState === 'active' && (
          <div className="absolute top-[55%] left-1/2 -translate-x-1/2 z-30 w-80 lg:w-96 animate-in fade-in slide-in-from-bottom-8 duration-500">
             <div className="bg-white/90 dark:bg-slate-900/90 backdrop-blur-xl border border-slate-200 dark:border-white/10 p-6 rounded-2xl shadow-2xl">
                <div className="flex justify-between items-start mb-4"><h3 className="text-slate-800 dark:text-white font-medium text-lg flex items-center gap-2"><Activity className="w-4 h-4 text-emerald-500 animate-pulse" /> System Online</h3><button onClick={(e) => { e.stopPropagation(); handleReset(); }}><X size={18} /></button></div>
                <button onClick={(e) => { e.stopPropagation(); onDeployClick(); }} className="w-full mt-6 bg-secondary text-white py-3 rounded-lg font-medium flex items-center justify-center gap-2 hover:bg-blue-600 transition-colors"><Cpu size={16} /> Deploy Agent</button>
             </div>
          </div>
        )}
        <svg viewBox="-100 -100 600 600" className="w-full h-full drop-shadow-2xl overflow-visible">
          <defs>
            <linearGradient id="capyGradient" x1="0%" y1="0%" x2="100%" y2="100%">
                <stop offset="0%" stopColor="#E79152" />
                <stop offset="100%" stopColor="#D97706" />
            </linearGradient>
            <filter id="iconShadow">
                <feDropShadow dx="0" dy="4" stdDeviation="6" floodOpacity="0.1"/>
            </filter>
            <filter id="glow">
                <feGaussianBlur stdDeviation="4" result="coloredBlur"/>
                <feMerge>
                    <feMergeNode in="coloredBlur"/>
                    <feMergeNode in="SourceGraphic"/>
                </feMerge>
            </filter>
          </defs>
          
          {/* Wave-like Shadow Background */}
          <g className="transition-all duration-1000 ease-in-out">
             <circle cx="200" cy="200" r={mascotState === 'active' ? "280" : "220"} fill={mascotState === 'active' ? accentColor : (darkMode ? "#1E293B" : "#a8a29e")} opacity={darkMode ? "0.1" : "0.05"} className="transition-all duration-1000" />
             <circle cx="200" cy="200" r={mascotState === 'active' ? "240" : "190"} fill={mascotState === 'active' ? accentColor : (darkMode ? "#334155" : "#78716c")} opacity={darkMode ? "0.1" : "0.05"} className="transition-all duration-1000 delay-75" />
             <circle cx="200" cy="200" r={mascotState === 'active' ? "200" : "160"} fill={mascotState === 'active' ? accentColor : (darkMode ? "#475569" : "#57534e")} opacity={darkMode ? "0.1" : "0.05"} className="transition-all duration-1000 delay-150" />
          </g>

          {mascotState === 'active' && <g className="animate-in fade-in duration-1000">{icons.map((item, i) => { const angle = (i * (360 / icons.length)) * (Math.PI / 180); return <line key={i} x1="200" y1="200" x2={200 + 260 * Math.cos(angle)} y2={200 + 260 * Math.sin(angle)} stroke={THEME.secondary} strokeWidth="2" strokeDasharray="4 4" opacity="0.3" />; })}</g>}
          
          <g transform="translate(0, 20)">
             <rect x="80" y="80" width="240" height="240" rx="90" fill="url(#capyGradient)" />
             <rect x="120" y="200" width="160" height="100" rx="45" fill={darkMode ? "rgba(255,255,255,0.1)" : "rgba(255,255,255,0.3)"} />
             <g>
                {mascotState === 'happy' ? (
                    <>
                        <circle cx="160" cy="165" r="12" fill="#0F172A" />
                        <circle cx="240" cy="165" r="12" fill="#0F172A" />
                    </>
                ) : mascotState === 'standby' ? (
                    <>
                        <path d="M130 160 Q160 170 190 160" fill="none" stroke="#0F172A" strokeWidth="7" strokeLinecap="round" />
                        <path d="M210 160 Q240 170 270 160" fill="none" stroke="#0F172A" strokeWidth="7" strokeLinecap="round" />
                    </>
                ) : (
                    <g className="animate-in zoom-in duration-300">
                        <rect x="110" y="140" width="180" height="50" rx="10" fill="#1e293b" stroke={accentColor} strokeWidth="2" />
                        <rect x="130" y="155" width="40" height="20" rx="4" fill={accentColor} className="animate-pulse" />
                        <rect x="230" y="155" width="40" height="20" rx="4" fill={accentColor} className="animate-pulse" />
                        <path d="M190 165 H 210" stroke={accentColor} strokeWidth="2" />
                    </g>
                )}
                <path d="M190 230 Q200 240 210 230" fill="none" stroke="#0F172A" strokeWidth="7" strokeLinecap="round" />
                {mascotState === 'happy' ? (
                    <path d="M170 250 Q200 280 230 250" fill="none" stroke="#0F172A" strokeWidth="6" strokeLinecap="round" />
                ) : (
                    <path d="M180 250 Q200 260 220 250" fill="none" stroke="#0F172A" strokeWidth="5" strokeLinecap="round" opacity="0.6" />
                )}
             </g>
             <g 
                style={{ 
                    transform: `translateY(${mascotState === 'active' || mascotState === 'happy' ? 160 : 0}px)`, 
                    transition: 'transform 0.6s cubic-bezier(0.22, 1, 0.36, 1)',
                    cursor: mascotState === 'standby' ? 'pointer' : 'default'
                }} 
             >
                <path d="M60 190 V150 C60 50 340 50 340 150 V190" fill="none" stroke={accentColor} strokeWidth="24" strokeLinecap="round" />
                <rect x="20" y="150" width="60" height="110" rx="18" fill={THEME.secondary} className="drop-shadow-xl" />
                <rect x="320" y="150" width="60" height="110" rx="18" fill={THEME.secondary} className="drop-shadow-xl" />
             </g>
          </g>
          {icons.map((item, i) => {
            const angle = (i * (360 / icons.length)) * (Math.PI / 180);
            return (
                <g 
                    key={i} 
                    transform={`translate(${200 + 260 * Math.cos(angle)}, ${200 + 260 * Math.sin(angle)})`} 
                    className={mascotState === 'standby' ? 'animate-float-slow' : 'transition-all duration-700 ease-out'}
                    style={{ 
                        animationDelay: `${i * 0.5}s`,
                        transform: mascotState === 'active' ? `translate(${200 + 260 * Math.cos(angle)}px, ${200 + 260 * Math.sin(angle)}px) scale(1.1)` : undefined 
                    }}
                >
                    <circle r="32" fill="white" filter="url(#iconShadow)" />
                    <circle r="36" fill="none" stroke={THEME.secondary} strokeWidth="2" opacity={mascotState === 'active' ? "1" : "0"} strokeDasharray="4 2" />
                    <g transform="translate(-12, -12)">
                        <item.Icon size={24} color={THEME.secondary} />
                    </g>
                </g>
            );
          })}
        </svg>
      </div>
    );
};

export default CapyBotMascot;
